import React, { useMemo, useState } from 'react';
import Select from 'react-select';
import { NimbusModal, NimbusModalContent } from '../components/NimbusModal';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { departmentsApi, peopleApi, usersApi, parseApiError } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { useSwal } from '../hooks/useSwal';
import { PageHeader } from '../components/PageHeader';
import { NimbusTable } from '../components/NimbusTable';
import { usePagination } from '../hooks/usePagination';
import { Pagination } from '../components/Pagination';
import { reactSelectStyles } from '../utils/reactSelectStyles';
import { toneFromMap, type Tone } from '../constants/tone';

declare const Swal: any;

type Department = { departmentId: string; name: string };

type User = {
  userId: string;
  name?: string;
  email?: string;
  role?: 'admin' | 'user' | 'viewer' | string;
  status?: string;
  createdAt?: string;
  departmentId?: string;
  departmentName?: string;
  /** Can sign off on Initiative Tracker projected end-date exceptions. */
  isExceptionApprover?: boolean;
};

type Person = {
  personId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  departmentId?: string | null;
};

type UsersResponse = {
  users?: User[];
  count?: number;
};

const USER_STATUS_TONE: Record<string, Tone> = { active: 'success', pending: 'warning', inactive: 'neutral' };

export const UsersPage: React.FC = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const swal = useSwal();

  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  const { data, isLoading, error } = useQuery<UsersResponse>({
    queryKey: ['users'],
    queryFn: () => usersApi.getAll(),
    enabled: !!user,
  });

  const { data: departments = [] } = useQuery<Department[]>({
    queryKey: ['departments'],
    queryFn: departmentsApi.getAll,
    enabled: isAdmin,
  });

  const { data: people = [] } = useQuery<Person[]>({
    queryKey: ['people'],
    queryFn: peopleApi.getAll,
    enabled: isAdmin,
  });

  const users = data?.users || [];

  // People already on the team, so the picker below only offers people who'd
  // actually be new -- avoids creating a duplicate account by accident.
  const takenEmails = useMemo(() => {
    const s = new Set<string>();
    users.forEach((u) => u.email && s.add(u.email.trim().toLowerCase()));
    return s;
  }, [users]);

  const invitablePeople = useMemo(
    () => people.filter((p) => p.email?.trim() && !takenEmails.has(p.email.trim().toLowerCase())),
    [people, takenEmails]
  );

  const currentUserId = user?.userId || '';

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const [inviteForm, setInviteForm] = useState({
    name: '',
    email: '',
    role: 'user',
    departmentId: '',
  });

  const [selectedPersonId, setSelectedPersonId] = useState('');

  const applyPersonSelection = (personId: string) => {
    setSelectedPersonId(personId);
    const person = people.find((p) => p.personId === personId);
    if (!person) return;
    setInviteForm((f) => ({
      ...f,
      name: [person.firstName, person.lastName].filter(Boolean).join(' ') || f.name,
      email: person.email || f.email,
      departmentId: person.departmentId || f.departmentId,
    }));
  };

  const openCreateModal = () => setShowCreateModal(true);

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setInviteForm({ name: '', email: '', role: 'user', departmentId: '' });
    setSelectedPersonId('');
  };

  const [roleForm, setRoleForm] = useState<{ userId: string; role: string; departmentId: string }>({
    userId: '',
    role: 'user',
    departmentId: '',
  });

  const openEditModal = (u: User) => {
    setRoleForm({ userId: u.userId, role: u.role || 'user', departmentId: u.departmentId || '' });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setRoleForm({ userId: '', role: 'user', departmentId: '' });
  };

  const deptNameById = useMemo(() => {
    const m = new Map<string, string>();
    departments.forEach((d) => m.set(d.departmentId, d.name));
    return m;
  }, [departments]);

  const { pageItems, page, setPage, totalPages, pageSize, totalItems } = usePagination(users, 25);

  const showTempPasswordDialog = (title: string, email: string, result: any) => {
    // Note: this html string renders inside SweetAlert2's own popup shell (a
    // third-party overlay outside our component tree), so it intentionally
    // keeps the app's plain Bootstrap classes rather than np- ones, matching
    // every other Swal dialog in the app (see hooks/useSwal.ts).
    const emailNote = result?.emailSent
      ? '<p class="text-muted small mb-0">An email with these details was also sent.</p>'
      : '<p class="text-muted small mb-0">Email could not be sent — share this password with them directly.</p>';

    Swal.fire({
      title,
      html: `
        <p>Give these sign-in details to <strong>${email}</strong>:</p>
        <div class="input-group mb-2">
          <span class="input-group-text">Email</span>
          <input class="form-control" readonly value="${email}">
        </div>
        <div class="input-group mb-2">
          <span class="input-group-text">Temp password</span>
          <input class="form-control" readonly value="${result?.temporaryPassword || ''}">
        </div>
        <p class="text-muted small">They'll be asked to set their own password on next sign-in.</p>
        ${emailNote}
      `,
      icon: 'success',
      confirmButtonText: 'Done',
      customClass: { confirmButton: 'np-btn np-btn-primary' },
      buttonsStyling: false,
    });
  };

  const inviteMutation = useMutation({
    mutationFn: async () => {
      const payload: any = {
        name: inviteForm.name,
        email: inviteForm.email,
        role: inviteForm.role,
      };
      if (inviteForm.departmentId) {
        payload.departmentId = inviteForm.departmentId;
        payload.departmentName = deptNameById.get(inviteForm.departmentId) || '';
      }
      return usersApi.invite(payload);
    },
    onSuccess: async (result: any) => {
      const createdEmail = inviteForm.email;
      setInviteForm({ name: '', email: '', role: 'user', departmentId: '' });
      setSelectedPersonId('');
      await qc.invalidateQueries({ queryKey: ['users'] });
      showTempPasswordDialog('Account created', createdEmail, result);
    },
    onError: (e: Error) => toast.error(`Failed to create account: ${parseApiError(e).message}`),
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (u: User) => usersApi.resetPassword(u.userId),
    onSuccess: (result: any, u: User) => {
      showTempPasswordDialog('Password reset', u.email || result?.email || '', result);
    },
    onError: (e: Error) => toast.error(`Failed to reset password: ${parseApiError(e).message}`),
  });

  const handleResetPassword = async (u: User) => {
    const ok = await swal.confirm(
      `Reset password for ${u.name || u.email}?`,
      "They'll need to sign in with a new temporary password and won't be able to use their old one."
    );
    if (!ok) return;
    resetPasswordMutation.mutate(u);
  };

  const updateRoleMutation = useMutation({
    mutationFn: async (payload: { userId: string; role: string; departmentId: string }) => {
      await usersApi.update(payload.userId, {
        role: payload.role,
        departmentId: payload.departmentId,
        departmentName: payload.departmentId ? deptNameById.get(payload.departmentId) || '' : '',
      });
    },
    onSuccess: async () => {
      setRoleForm({ userId: '', role: 'user', departmentId: '' });
      await qc.invalidateQueries({ queryKey: ['users'] });
      toast.success('User updated.');
    },
    onError: (e: Error) => toast.error(`Failed to update user: ${parseApiError(e).message}`),
  });

  const toggleApproverMutation = useMutation({
    mutationFn: async (u: User) => {
      await usersApi.update(u.userId, { isExceptionApprover: !u.isExceptionApprover });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['users'] });
      toast.success('Updated.');
    },
    onError: (e: Error) => toast.error(`Failed to update: ${parseApiError(e).message}`),
  });

  const removeUserMutation = useMutation({
    mutationFn: async (u: User) => {
      await usersApi.delete(u.userId);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['users'] });
      toast.success('User removed.');
    },
    onError: (e: Error) => toast.error(`Failed to remove user: ${parseApiError(e).message}`),
  });

  const handleRemoveUser = async (u: User) => {
    const ok = await swal.confirmDelete(u.name || u.email || u.userId);
    if (!ok) return;
    removeUserMutation.mutate(u);
  };


  if (!isAdmin) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="np-banner np-tone-warning mb-0">
          <i className="ri-error-warning-line"></i>
          Access denied. Only administrators can access team management.
        </div>
      </div>
    );
  }

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-group-line"
        title="Users &amp; Access"
        subtitle={typeof data?.count === 'number' ? `${data.count} member${data.count !== 1 ? 's' : ''}` : undefined}
        colour="primary"
        actions={
          <button className="np-btn np-btn-primary" onClick={openCreateModal}>
            <i className="ri-add-line"></i>Create Account
          </button>
        }
      />

      <div className="np-card">
        <div className="np-card-body">
          {isLoading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="np-banner np-tone-danger mb-0">
              <i className="ri-error-warning-line"></i>
              Failed to load users.
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-5">
              <i className="ri-inbox-line ri-48px d-block mb-2" style={{ color: 'var(--bs-secondary-color)' }}></i>
              <p className="np-sub mb-0">No users found.</p>
            </div>
          ) : (
            <NimbusTable thead={<tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th style={{ width: '120px' }}>Role</th>
                    <th style={{ width: '160px' }}>Department</th>
                    <th style={{ width: '120px' }}>Status</th>
                    <th style={{ width: '160px' }}>Joined</th>
                    <th style={{ width: '150px' }} title="Can sign off on Initiative Tracker projected end-date exceptions">Exception Approver</th>
                    <th style={{ width: '120px' }}></th>
                  </tr>}>
                  {pageItems.map((u) => {
                    const isCurrent = u.userId === currentUserId;
                    return (
                      <tr key={u.userId}>
                        <td className="np-row-title-static">{u.name || u.userId}{isCurrent ? ' (You)' : ''}</td>
                        <td className="np-sub">{u.email || '-'}</td>
                        <td>{u.role ? <span className="np-chip np-tone-neutral">{u.role}</span> : <span className="np-sub">—</span>}</td>
                        <td>{u.departmentName || <span className="np-sub">All registers</span>}</td>
                        <td>{u.status ? <span className={`np-chip np-tone-${toneFromMap(USER_STATUS_TONE, u.status, 'neutral')}`}>{u.status}</span> : <span className="np-sub">—</span>}</td>
                        <td className="np-tabular">{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '-'}</td>
                        <td>
                          <label className="np-switch mb-0">
                            <input
                              type="checkbox"
                              checked={!!u.isExceptionApprover}
                              disabled={toggleApproverMutation.isPending}
                              onChange={() => toggleApproverMutation.mutate(u)}
                            />
                            <span className="np-switch-track"></span>
                            <span className="np-switch-thumb"></span>
                          </label>
                        </td>
                        <td>
                          {isCurrent ? (
                            <span className="np-sub">—</span>
                          ) : (
                            <div className="d-flex gap-1">
                              <button
                                className="np-icon-btn"
                                onClick={() => openEditModal(u)}
                                title="Edit"
                              >
                                <i className="ri-edit-line"></i>
                              </button>
                              <button
                                className="np-icon-btn"
                                onClick={() => handleResetPassword(u)}
                                disabled={resetPasswordMutation.isPending}
                                title="Reset Password"
                              >
                                <i className="ri-key-2-line"></i>
                              </button>
                              <button
                                className="np-icon-btn np-icon-btn-danger"
                                onClick={() => handleRemoveUser(u)}
                                disabled={removeUserMutation.isPending}
                                title="Remove"
                              >
                                <i className="ri-delete-bin-line"></i>
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
            </NimbusTable>
          )}
          {pageItems.length > 0 && (
            <Pagination page={page} totalPages={totalPages} pageSize={pageSize} totalItems={totalItems} onPageChange={setPage} />
          )}
        </div>
      </div>

      {showCreateModal && (
        <NimbusModal>
          <NimbusModalContent
            title="Create Account"
            onClose={closeCreateModal}
            footer={
              <>
                <button type="button" className="np-btn np-btn-ghost" onClick={closeCreateModal}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="np-btn np-btn-primary"
                  onClick={() => { inviteMutation.mutate(); closeCreateModal(); }}
                  disabled={inviteMutation.isPending || !inviteForm.email.trim() || !inviteForm.name.trim()}
                >
                  {inviteMutation.isPending ? 'Creating…' : 'Create Account'}
                </button>
              </>
            }
          >
            {invitablePeople.length > 0 && (
              <div className="np-field mb-3">
                <label>Fill from People register</label>
                <Select
                  options={invitablePeople.map((p) => ({
                    value: p.personId,
                    label: [p.firstName, p.lastName].filter(Boolean).join(' ') || p.personId,
                    email: p.email || '',
                  }))}
                  value={
                    selectedPersonId
                      ? (() => {
                          const p = invitablePeople.find((x) => x.personId === selectedPersonId);
                          return p
                            ? { value: p.personId, label: [p.firstName, p.lastName].filter(Boolean).join(' ') || p.personId, email: p.email || '' }
                            : null;
                        })()
                      : null
                  }
                  onChange={(opt) => applyPersonSelection(opt?.value ?? '')}
                  formatOptionLabel={(opt: any) => (
                    <span>
                      {opt.label}
                      {opt.email ? <span className="np-sub"> ({opt.email})</span> : null}
                    </span>
                  )}
                  placeholder="Search people by name…"
                  isClearable
                  isSearchable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={reactSelectStyles()}
                  noOptionsMessage={() => 'No available people to invite'}
                />
                <p className="np-hint">Fills in name, email, and department below — just pick the account type and create.</p>
              </div>
            )}
            <div className="row g-3">
              <div className="col-12 col-md-6 np-field">
                <label>Name</label>
                <input className="np-input" value={inviteForm.name} onChange={(e) => setInviteForm((f) => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="col-12 col-md-6 np-field">
                <label>Email</label>
                <input
                  className="np-input"
                  value={inviteForm.email}
                  onChange={(e) => {
                    setSelectedPersonId('');
                    setInviteForm((f) => ({ ...f, email: e.target.value }));
                  }}
                />
              </div>
              <div className="col-12 col-md-6 np-field">
                <label>Role</label>
                <select className="np-select" value={inviteForm.role} onChange={(e) => setInviteForm((f) => ({ ...f, role: e.target.value }))}>
                  <option value="admin">admin</option>
                  <option value="user">user</option>
                  <option value="viewer">viewer</option>
                </select>
              </div>
              <div className="col-12 col-md-6 np-field">
                <label>Department</label>
                <select className="np-select" value={inviteForm.departmentId} onChange={(e) => setInviteForm((f) => ({ ...f, departmentId: e.target.value }))}>
                  <option value="">No Department</option>
                  {departments.map((d) => (
                    <option key={d.departmentId} value={d.departmentId}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </NimbusModalContent>
        </NimbusModal>
      )}

      {showEditModal && (
        <NimbusModal>
          <NimbusModalContent
            title="Edit User"
            onClose={closeEditModal}
            footer={
              <>
                <button type="button" className="np-btn np-btn-ghost" onClick={closeEditModal}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="np-btn np-btn-primary"
                  onClick={() => { updateRoleMutation.mutate(roleForm); closeEditModal(); }}
                  disabled={updateRoleMutation.isPending}
                >
                  {updateRoleMutation.isPending ? 'Saving…' : 'Save Changes'}
                </button>
              </>
            }
          >
            <div className="row g-3">
              <div className="col-12 col-md-6 np-field">
                <label>Role</label>
                <select
                  className="np-select"
                  value={roleForm.role}
                  onChange={(e) => setRoleForm((f) => ({ ...f, role: e.target.value }))}
                >
                  <option value="admin">admin</option>
                  <option value="user">user</option>
                  <option value="viewer">viewer</option>
                </select>
              </div>
              <div className="col-12 col-md-6 np-field">
                <label>Department</label>
                <select
                  className="np-select"
                  value={roleForm.departmentId}
                  onChange={(e) => setRoleForm((f) => ({ ...f, departmentId: e.target.value }))}
                  title="Restricts this user to registers assigned to this department (admins see everything regardless)"
                >
                  <option value="">All registers</option>
                  {departments.map((d) => (
                    <option key={d.departmentId} value={d.departmentId}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </NimbusModalContent>
        </NimbusModal>
      )}
    </div>
  );
};
